import React, { useState, useCallback } from 'react'
import { StyleSheet, View } from 'react-native'
import { Text } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { useNodeTopPerspectivesQuery } from '../../../../generated/graphql'
import { PerspectiveStatRow } from '../components/perspective-stats.row'
import {
  ExportButton,
  ExportBanner,
} from '../../../../@ui/buttons/export.button'
import { SortOption, SortOptions } from '../../../../@ui/buttons/sort.button'
import { useExportPerspectives } from '../hooks/export-node-perspective.hook'
import Row from '../../../../@ui/containers/grid/row'
import PaginatedFlatList from '../../../../@ui/containers/lists/paginated-flat.list'

interface PerspectiveStatsTabProps {
  nodeId: string
  nodeName: string
}

const sortOptions = (
  t: TFunction<'translation', undefined>
): SortOption<string>[] => [
  {
    field: 'votes',
    label: t('sort-most-votes'),
    direction: 'DESC',
    icon: 'sort-numeric-descending',
  },
  {
    field: 'votes',
    label: t('sort-least-votes'),
    direction: 'ASC',
    icon: 'sort-numeric-ascending',
  },
  {
    field: 'title',
    label: t('sort-title-a-z'),
    direction: 'ASC',
    icon: 'sort-alphabetical-ascending',
  },
  {
    field: 'title',
    label: t('sort-title-z-a'),
    direction: 'DESC',
    icon: 'sort-alphabetical-descending',
  },
  {
    field: 'date',
    label: t('sort-newest-first'),
    direction: 'DESC',
    icon: 'sort-calendar-descending',
  },
  {
    field: 'date',
    label: t('sort-oldest-first'),
    direction: 'ASC',
    icon: 'sort-calendar-ascending',
  },
]

export function PerspectiveStatsTab({
  nodeId,
  nodeName,
}: PerspectiveStatsTabProps) {
  const { t } = useTranslation('nodes')
  const [limit] = useState(30)
  const [sortField, setSortField] = useState<'votes' | 'title' | 'date'>(
    'votes'
  )
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC')

  const { exportPerspectives, isExporting, progress } = useExportPerspectives({
    nodeId,
    nodeName,
    sortBy: sortField,
    sort: sortDirection,
  })

  const { data, loading, refetch, error, fetchMore } =
    useNodeTopPerspectivesQuery({
      variables: {
        input: {
          nodeId,
          limit,
          offset: 0,
          sortBy: sortField,
          sort: sortDirection,
        },
      },
    })

  const perspectives = data?.nodeTopPerspectives?.perspectives || []
  const total = data?.nodeTopPerspectives?.total || 0

  // Debug logging
  if (error) {
    console.error('Perspective stats query error:', error)
  }
  if (data) {
    console.log('Perspective stats data:', {
      total,
      perspectivesCount: perspectives.length,
    })
  }

  const handleSortChange = useCallback(
    (field: string, direction: 'ASC' | 'DESC') => {
      setSortField(field as 'votes' | 'title' | 'date')
      setSortDirection(direction)
    },
    []
  )

  const handleLoadMore = useCallback(
    async (params: { offset: number; limit: number }) => {
      if (loading) return

      await fetchMore({
        variables: {
          input: {
            nodeId,
            limit: params.limit,
            offset: params.offset,
            sortBy: sortField,
            sort: sortDirection,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          return {
            nodeTopPerspectives: {
              ...fetchMoreResult.nodeTopPerspectives,
              perspectives: [
                ...prev.nodeTopPerspectives.perspectives,
                ...fetchMoreResult.nodeTopPerspectives.perspectives,
              ],
            },
          }
        },
      })
    },
    [nodeId, loading, fetchMore, sortField, sortDirection]
  )

  const handleRefresh = useCallback(async () => {
    await refetch({
      input: {
        nodeId,
        limit,
        offset: 0,
        sortBy: sortField,
        sort: sortDirection,
      },
    })
  }, [refetch, nodeId, limit, sortField, sortDirection])

  return (
    <View style={styles.container}>
      <ExportBanner
        visible={isExporting}
        progress={progress}
        message={t('exporting-perspectives')}
      />

      <View style={styles.header}>
        <Row justifyContent="space-between" alignItems="center">
          <Text variant="titleMedium">
            {t('perspective-stats')} ({total})
          </Text>
          <Row gap={1}>
            <ExportButton
              onPress={exportPerspectives}
              disabled={isExporting || !perspectives.length}
              tooltip={t('export-perspectives-to-csv')}
            />
            <SortOptions
              selectedField={sortField}
              selectedDirection={sortDirection}
              onSortChange={handleSortChange}
              options={sortOptions(t)}
            />
          </Row>
        </Row>
      </View>

      <PaginatedFlatList
        data={perspectives}
        total={total}
        initialLimit={limit}
        style={styles.listContainer}
        contentContainerStyle={styles.listContent}
        renderItem={({ item, index }) => {
          const perspective = item as NonNullable<(typeof perspectives)[0]>
          return (
            <PerspectiveStatRow
              perspective={perspective}
              rank={perspective.dbPosition || index + 1}
            />
          )
        }}
        keyExtractor={(item) => {
          const perspective = item as NonNullable<(typeof perspectives)[0]>
          return perspective.id
        }}
        onLoadMore={handleLoadMore}
        onRefresh={handleRefresh}
        emptyMessage={t('no-perspectives-found')}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  listContainer: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingTop: 8,
  },
})
